import { Button, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import * as Yup from 'yup';
import { SiTrackPage } from '../../components/si-track-page';
import { SelectChainItem, SelectConfig, SelectResult } from '../../components/select/select-chain.component';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { projectRequestColumns } from './project-request-columns';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { UserPermissionName } from '../../models/user-permission-name';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { WorkOrderReport } from '../../models/work-order-report';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { projectsApiKeyStore } from '../../api/projects-api-key-store';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    projectID: params.get('projectID'),
    unitID: params.get('unitID'),
  };
};

export const ProjectRequestReportPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilter, setSelectedFilter] = useState<SelectResult>(getFiltesFromUrl(searchParams));

  const form = useReactHookForm({
    defaultValues: {}
  });

  const getAPIKeyStore = () => {
    const { customerID, officeID } = selectedFilter;

    if (customerID) {
      return projectsApiKeyStore.getCustomerProjectRequest(customerID);
    }
    if (officeID) {
      return projectsApiKeyStore.getOfficeProjectRequest(officeID);
    }

    return projectsApiKeyStore.getAllProjectRequest();
  };

  const { isLoading, data: projectsRequestData, refetch } = useLoadData<ApiResponse<any[]>>(getAPIKeyStore());

  const handleFilterSelected = (filter: SelectResult) => {
    setSelectedFilter(filter);

    if (filter) {
      setSearchParams({
        ...(filter.customerID && { customerID: filter.customerID }),
        ...(filter.officeID && { officeID: filter.officeID }),
      });
    }
  };

  const schedulingRedirect = (requestID: any) => {
    const requestType = "mysi"
    // navigate(`/dashboard`);
    navigate(`/projects/edit/0?reqtype=${requestType}&reqid=${requestID}`);
  }

  const redirectProjectRequestElement = (dataItem: any) => {
    return (
      // <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
      <IconButton aria-label="move" onClick={() => schedulingRedirect(dataItem?.dataItem?.requestID)}>
        <Tooltip title={<Typography variant="body1">View</Typography>}>
          <RemoveRedEyeIcon />
        </Tooltip>
      </IconButton>
      // </GuardedComponent>
    );
  }

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout
  } = useLoadData<ApiResponse<GridPreference>>(
    gridApiKeyStore.getGridLayout('ProjectRequest', 'ProjectRequest')
  );

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredWorkOrderReports, setFilteredWorkOrderReports] = useState<WorkOrderReport[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredWorkOrderReportList = projectsRequestData?.data?.filter((WorkOrderReport: any) =>
      Object.values(WorkOrderReport).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredWorkOrderReports(filteredWorkOrderReportList || []);
  }, [debouncedSearchTerm, projectsRequestData?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'ProjectRequest',
      pageName: 'ProjectRequest',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'ProjectRequest',
      pageName: 'ProjectRequest',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <SiTrackPage title="Project Request">
      <ReactHookForm formName="PROJECT_REQUEST_FORM" onSubmit={() => { }} formContextProps={form}>
        <Grid container spacing={1}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <SelectChainItem
              config={[
                {
                  title: 'Customer',
                  optionsLabelKey: 'customerName',
                  optionsValueKey: 'customerID',
                  type: 'autocomplete',
                  getQueryKeyConfig: () => customersApiKeyStore.getCustomers(),
                },
                {
                  title: 'Office',
                  optionsLabelKey: 'officeName',
                  optionsValueKey: 'officeID',
                  type: 'autocomplete',
                  getQueryKeyConfig: (value?: SelectResult) => officeApiKeyStore.getCustomerOffices(value?.customerID),
                },
              ]}
              value={selectedFilter}
              onSelect={handleFilterSelected}
            />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Paper elevation={0} style={styling.paperStyle}>
              <SiTrackGrid
                fileTitle='project_request'
                columns={projectRequestColumns}
                dataItemKey="requestID"
                CustomComponent={redirectProjectRequestElement}
                actionPermissions={{
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
                isBusy={isLoading || isLoadingGridLayout}

                // -----------------Grid Layout-------
                dataItems={filteredWorkOrderReports || []}
                reorderable
                handleSearch={handleInputChange}
                handleSaveLayout={handleSaveLayout}
                isSaveLayout={true}
                gridConfigFromApi={gridConfig}
                handleResetLayout={handleResetLayout}
              // -----------------Grid Layout-------
              />
            </Paper>
          </Grid>
        </Grid>
      </ReactHookForm>
    </SiTrackPage>
  );
};
